/* global dataUrl, managePackUrl */

import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import BudgetTagsCell from '../../budget_tags/BudgetTagsCell';
import BudgetTagsItemModal from '../../budget_tags/BudgetTagsItemModal';

import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';
import NumberFormat from 'CommonComponents/LocalisedNumberFormat';

export default function PackTable(props) {
    const [items, setItems] = useState([]);
    const [venues, setVenues] = useState([]);
    const [pack, setPack] = useState([]);
    const [finalStates, setFinalStates] = useState({});
    const [loading, setLoading] = useState(false);
    const [budgetTagsEditable, setBudgetTagsEditable] = useState(false);
    const [budgetTagsTarget, setBudgetTagsTarget] = useState(null);
    const closeBudgetTags = useCallback(() => setBudgetTagsTarget(null), []);
    const budgetTagsSaved = useCallback(
        (id, cell) => setItems((current) => current.map((item) => (item.item.id === id ? { ...item, budget_tags: cell } : item))),
        [],
    );

    // Re-fetch whenever the data URL changes so the table (and its checkbox "selected"
    // state) always reflects the persisted server state. The edit modal that hosts this
    // table stays mounted between opens; depending on [] alone would leave stale
    // finalStates (e.g. a checkbox still showing "selected" after an unselect+save was
    // persisted). Keying off dataUrl re-syncs on every (re)open of a pack line.
    useEffect(() => {
        if (!props.dataUrl) {
            return;
        }
        setLoading(true);
        axios
            .get(props.dataUrl)
            .then((res) => {
                const { data } = res;
                setFinalStates(Object.prototype.hasOwnProperty.call(data, 'states') ? data.states : {});
                setVenues(data.venues);
                setItems(data.items);
                setPack(data.pack);
                setBudgetTagsEditable(!!data.budgetTagsEditable);
            })
            .catch((e) => {
                console.log(e);
            })
            .finally(() => setLoading(false));
    }, [props.dataUrl]);

    const toggleFinalState = (venueId) => {
        setFinalStates((state) => ({
            ...state,
            venues: {
                ...state.venues,
                [venueId]: {
                    ...state.venues[venueId],
                    isSelected: !state.venues[venueId].isSelected,
                },
            },
        }));
    };

    const save = () => {
        setLoading(true);
        const data = {
            finalStates: finalStates,
        };
        axios({
            method: 'PUT',
            url: props.updateUrl,
            data,
        })
            .then((response) => {
                if (props.successHandler) {
                    props.successHandler(response);
                }
            })
            .catch((error) => {
                console.log('error: ', error);
            })
            .finally(() => setLoading(false));
    };

    return (
        <div className="table__card">
            {props.editable && (
                <div className="tw-flex tw-flex-row tw-w-full tw-justify-end tw-mb-3">
                    <Button small variant="primary" style={{ minWidth: 'fit-content' }} disabled={loading} onClick={save}>
                        {$t('Save')}
                    </Button>
                </div>
            )}
            <div className="inbud-table_wrapper">
                <table className="inbud-table inbud-table_first-sticky inbud-table_first-shadow tw-table-fixed">
                    <thead>
                        <tr>
                            <th rowSpan={2} className="tw-w-40 !tw-align-top !tw-font-extrabold tw-uppercase">
                                {$t('Total')}: <NumberFormat value={pack.total || 0} displayType={'text'} thousandSeparator={true} decimalScale={0} />
                            </th>
                            {venues.map((venue) => {
                                return (
                                    <th key={`venue-${venue.venue.id}`} className="tw-w-48 !tw-pb-1.5 !tw-font-extrabold tw-uppercase">
                                        {$t('Total')}: <NumberFormat value={venue.total} displayType={'text'} thousandSeparator={true} decimalScale={0} />
                                    </th>
                                );
                            })}
                        </tr>
                        <tr>
                            {venues.map((venue) => {
                                return (
                                    <th key={`venue-${venue.venue.id}`} className="!tw-pt-1.5 !tw-align-top !tw-static">
                                        <div className="tw-flex tw-flex-row tw-justify-center tw-items-center tw-gap-x-2 tw-w-full">
                                            {props.editable && !finalStates.venues[venue.venue.id].isDisabled && (
                                                <CheckboxInput
                                                    id={venue.venue.id}
                                                    checked={finalStates.venues[venue.venue.id].isSelected}
                                                    label=""
                                                    onChange={() => toggleFinalState(venue.venue.id)}
                                                />
                                            )}
                                            {venue.venue.name}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => {
                            return (
                                <tr key={`item-${item.item.id}`}>
                                    <td className="tw-font-semibold tw-align-top">
                                        <div>
                                            {$t('Total')}:{' '}
                                            <NumberFormat value={item.item.total || 0} displayType={'text'} thousandSeparator={true} decimalScale={0} />
                                        </div>
                                        <div>
                                            {$t('Count')}:{' '}
                                            <NumberFormat value={item.item.totalAmount || 0} displayType={'text'} thousandSeparator={true} decimalScale={0} />
                                        </div>
                                        <div>
                                            {item.item.name} ({item.item.price})
                                        </div>
                                        {props.budgetTagsUrl && (
                                            <div className="tw-mt-1 tw-font-normal">
                                                <BudgetTagsCell
                                                    cell={item.budget_tags}
                                                    onOpen={() => setBudgetTagsTarget({ id: item.item.id, name: item.item.name, packId: pack.id })}
                                                />
                                            </div>
                                        )}
                                    </td>

                                    {venues.map((venue) => {
                                        const venueId = venue.venue.id;
                                        const venueData = venueId in item.venues ? item.venues[venueId] : null;

                                        return (
                                            <td key={`item-${item.item.id}-venue-${venueId}`}>
                                                {props.editable ? (
                                                    <div className="tw-flex tw-flex-col">
                                                        <div className="tw-h-8 tw-px-2 tw-flex tw-items-center tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-text-charcoal tw-mb-1">
                                                            <span className="tw-pr-2">#</span>
                                                            <span>{venueData ? venueData.amount : 0}</span>
                                                        </div>
                                                        <div className="tw-h-8 tw-px-2 tw-flex tw-items-center tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-text-charcoal tw-mb-1">
                                                            {venueData ? venueData.total : 0}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <a
                                                        href={managePackUrl + '?venue_id=' + venueId}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="tw-flex tw-flex-col"
                                                    >
                                                        <div className="tw-h-8 tw-px-2 tw-flex tw-items-center tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-text-charcoal tw-mb-1">
                                                            <span className="tw-pr-2">#</span>
                                                            <span>{venueData ? venueData.amount : 0}</span>
                                                        </div>
                                                        <div className="tw-h-8 tw-px-2 tw-flex tw-items-center tw-rounded-lg tw-text-sm tw-bg-grey/50 tw-text-charcoal tw-mb-1">
                                                            {venueData ? venueData.total : 0}
                                                        </div>
                                                    </a>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {props.budgetTagsUrl && (
                <BudgetTagsItemModal
                    id="pack-item-budget-tags-modal"
                    url={props.budgetTagsUrl}
                    target={budgetTagsTarget}
                    editable={budgetTagsEditable && !!items.find((item) => item.item.id === budgetTagsTarget?.id)?.budget_tags?.editable}
                    onClose={closeBudgetTags}
                    onSaved={budgetTagsSaved}
                />
            )}
        </div>
    );
}

if (document.getElementById('PackTable')) {
    var rootEl = document.getElementById('PackTable');
    ReactDOM.render(
        <App>
            <PackTable
                editable={false}
                dataUrl={dataUrl}
                cellW={rootEl.getAttribute('data-cellW')}
                cellH={rootEl.getAttribute('data-cellH')}
                dividerW={rootEl.getAttribute('data-dividerW')}
                dividerH={rootEl.getAttribute('data-dividerH')}
                budgetTagsUrl={rootEl.getAttribute('data-budget-tags-url')}
            />
        </App>,
        rootEl,
    );
}
